import { useCallback, useEffect, useRef, useState } from "react";
import { getContext, playBinaural, playTone, stopAll, unlockAudio } from "@/lib/audioEngine";
import { frequencyLabel, playableFrequency, presetName } from "@/lib/archiveCatalog";

const MAX_LAYERS = 12;

export const useArchiveAudio = () => {
  const [active, setActive] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const state = useRef(null);
  const generation = useRef(0);
  const timer = useRef(null);
  const owned = useRef(false);

  const stop = useCallback(() => {
    generation.current += 1;
    clearTimeout(timer.current);
    stopAll();
    owned.current = false;
    state.current = null;
    setActive(null);
    setPending(false);
    setError("");
  }, []);

  useEffect(() => () => {
    generation.current += 1;
    clearTimeout(timer.current);
    if (owned.current) stopAll();
  }, []);

  const start = async (build) => {
    const request = ++generation.current;
    setPending(true);
    setError("");
    try {
      // Resume inside the click gesture, including Safari's first-use unlock.
      const context = getContext();
      await Promise.all([unlockAudio(), context.resume()]);
      if (request !== generation.current) return;
      clearTimeout(timer.current);
      stopAll();
      owned.current = true;
      const next = build();
      state.current = next;
      setActive(next);
    } catch {
      if (request !== generation.current) return;
      stopAll();
      state.current = null;
      setActive(null);
      setError("Audio could not start. Please try playback again.");
    } finally {
      if (request === generation.current) setPending(false);
    }
  };

  const tones = (frequencies, label, append = false) => {
    const valid = frequencies.filter(playableFrequency);
    if (!valid.length) {
      setError("This set is outside the supported audio range (0–20,000 Hz).");
      return;
    }
    const previous = append && state.current?.kind === "tones" ? state.current.frequencies : [];
    const combined = [...new Set([...previous, ...valid])];
    if (combined.length > MAX_LAYERS) {
      setError("The stack is full (12 tones). Stop playback before starting another set.");
      return;
    }
    start(() => {
      combined.forEach(freq => playTone({ freq, gain: 0.2 / combined.length }));
      return {
        kind: "tones", frequencies: combined,
        label: append ? "TONE STACK" : label,
        detail: `${combined.length} ${combined.length === 1 ? "TONE" : "TONES"} · ${combined.map(frequencyLabel).join(" / ")}`,
      };
    });
  };

  const preset = item => {
    if (state.current?.id === item.id) { stop(); return; }
    start(() => {
      playBinaural({ carrierHz: item.carrier_hz, beatHz: item.beat.beat_hz, seconds: 300, gain: 0.2 });
      timer.current = setTimeout(stop, 300000);
      return { kind: "preset", id: item.id, label: presetName(item), detail: `STEREO · ${frequencyLabel(item.carrier_hz)} CARRIER / ${frequencyLabel(item.beat.beat_hz)} BEAT · 5 MIN` };
    });
  };

  return { active, pending, error, stop, tones, preset };
};